import React, { useState, useEffect } from 'react';
import { Form, Select, Flex, Typography, Tooltip, InputNumber } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Control, Controller, useWatch, useFormContext } from 'react-hook-form';
import useOrganization from '../../../../../Hooks/useOrganization';
import {
  getCurrencyInfo,
  getCurrencyOptionsForDropdown,
  formatCurrencyDisplay,
  CurrencyInfo,
} from '../../../../../Utils/currencyFormatter';
import { numberToWords } from '../../../../../Utils/numberToWords';
import { JobPostFormData } from '../../../schemas/jobPostValidation';
import './CTCField.scss';

interface CTCFieldProps {
  control: Control<JobPostFormData>;
}

const CTCField: React.FC<CTCFieldProps> = ({ control }) => {
  const { org } = useOrganization();
  const { setValue } = useFormContext<JobPostFormData>();
  const [currencyCode, setCurrencyCode] = useState<string>(org?.currency || 'INR');
  const [currencyInfo, setCurrencyInfo] = useState<CurrencyInfo | null>(null);
  const [currencyOptions, setCurrencyOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [showFormatted, setShowFormatted] = useState(false);
  
  // Watch form fields using React Hook Form
  const ctcValue = useWatch({ control, name: 'step1.ctc' });
  const currencyValue = useWatch({ control, name: 'step1.currencyCode' });
  const currencyFieldValue = useWatch({ control, name: 'step1.currency' });

  // Initialize currency options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const options = await getCurrencyOptionsForDropdown();
        setCurrencyOptions(options);
      } catch (error) {
        console.error('Error loading currency options:', error);
      }
    };
    loadOptions();
  }, []);

  // Initialize currency info and sync with form
  useEffect(() => {
    const initCurrency = async () => {
      try {
        const currency = currencyValue || org?.currency || 'INR';
        
        if (currency !== currencyCode) {
          setCurrencyCode(currency);
        }
        
        // Sync currency field with currencyCode if not already set
        if (!currencyFieldValue && currency) {
          setValue('step1.currency', currency, { shouldValidate: false });
        }
        
        const info = await getCurrencyInfo(currency);
        setCurrencyInfo(info);
      } catch (error) {
        console.error('Error initializing currency:', error);
        const info = await getCurrencyInfo('INR');
        setCurrencyInfo(info);
      }
    };
    initCurrency();
  }, [org?.currency, currencyValue, currencyFieldValue, setValue, currencyCode]);

  // Update currency info when currency changes
  useEffect(() => {
    const updateCurrencyInfo = async () => {
      const currencyToUse = currencyValue || currencyCode;
      if (currencyToUse) {
        const info = await getCurrencyInfo(currencyToUse);
        setCurrencyInfo(info);
      }
    };
    updateCurrencyInfo();
  }, [currencyCode, currencyValue]);

  // Handle blur - show formatted display
  const handleAmountBlur = () => {
    if (ctcValue !== null && ctcValue !== undefined && Number(ctcValue) > 0) {
      setShowFormatted(true);
    }
  };

  // Handle focus - hide formatted display
  const handleAmountFocus = () => {
    setShowFormatted(false);
  };

  if (!currencyInfo) {
    return null;
  }

  // Get the actual form value for display
  const displayAmount = ctcValue !== null && ctcValue !== undefined && !isNaN(Number(ctcValue)) ? Number(ctcValue) : null;
  const numericAmount = displayAmount || 0;
  const wordsText = numericAmount > 0 ? numberToWords(numericAmount, currencyCode) + ' per annum' : '';

  // Generate dynamic placeholder based on currency code
  const getPlaceholder = () => {
    const currency = currencyValue || currencyCode || 'INR';
    
    // Currency-specific example amounts (in thousands)
    const examples: Record<string, number> = {
      'INR': 260000,
      'USD': 50000,
      'EUR': 45000,
      'GBP': 40000,
      'AUD': 70000,
      'CAD': 60000,
      'SGD': 60000,
      'JPY': 5000000,
      'CNY': 200000,
      'AED': 120000,
    };
    
    const exampleAmount = examples[currency.toUpperCase()] || examples['USD'];
    return `Enter amount (e.g., ${exampleAmount.toLocaleString()})`;
  };

  return (
    <>
      {/* Currency and Compensation - Combined in one unit */}
      <Form.Item
        label={
          <Flex align="center" gap={4}>
            <span>Compensation</span>
            <Tooltip title="Enter the total compensation amount per annum">
              <InfoCircleOutlined className="info-icon" />
            </Tooltip>
          </Flex>
        }
      >
        <Flex gap={8} align="center">
          {/* Currency Selector */}
          <Controller
            name="step1.currencyCode"
            control={control}
            defaultValue={org?.currency || 'INR'}
            render={({ field }) => (
              <Select
                {...field}
                showSearch
                placeholder="Currency"
                onChange={(value) => {
                  field.onChange(value);
                  setCurrencyCode(value);
                  setShowFormatted(false);
                  // Update the currency field when currencyCode changes
                  setValue('step1.currency', value);
                }}
                filterOption={(input, option) => {
                  const label = option?.label ?? '';
                  const searchText = input.toLowerCase().trim();
                  const normalizedSearch = searchText
                    .replace(/\bof\b/gi, '')
                    .replace(/\s+/g, ' ')
                    .trim();
                  const normalizedLabel = label.toLowerCase();
                  return normalizedLabel.includes(searchText) || 
                         normalizedLabel.includes(normalizedSearch) ||
                         normalizedLabel.replace(/\s+/g, ' ').includes(normalizedSearch);
                }}
                options={currencyOptions}
                className="ctc-currency-select"
                style={{ width: 180 }}
              />
            )}
          />

          {/* CTC Amount */}
          <Controller
            name="step1.ctc"
            control={control}
            render={({ field }) => (
              <InputNumber
                {...field}
                value={field.value ?? null}
                onChange={(value) => {
                  const numValue = value !== null && value !== undefined ? Math.floor(value) : null;
                  field.onChange(numValue);
                  setShowFormatted(false);
                }}
                onBlur={(e) => {
                  field.onBlur();
                  handleAmountBlur();
                }}
                onFocus={(e) => {
                  handleAmountFocus();
                }}
                suffix={<span className="ctc-suffix">per annum</span>}
                placeholder={getPlaceholder()}
                onKeyDown={(e) => {
                  const allowedKeys = [
                    'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                    'Tab', 'Escape', 'Enter', 'Home', 'End'
                  ];
                  
                  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
                    return;
                  }
                  
                  if (e.key && !/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  const numericOnly = pastedText.replace(/[^0-9]/g, '');
                  if (numericOnly) {
                    const numValue = Number.parseInt(numericOnly, 10);
                    if (!isNaN(numValue) && numValue >= 0) {
                      field.onChange(numValue);
                    }
                  }
                }}
                controls={false}
                min={0}
                precision={0}
                className="ctc-amount-input"
                style={{ flex: 1 }}
              />
            )}
          />
        </Flex>
      </Form.Item>

      {/* Number to Words Hint */}
      {displayAmount && displayAmount > 0 && !showFormatted && (
        <Typography.Text type="secondary" style={{ marginTop: -8, marginBottom: 8, display: 'block' }}>
          {wordsText}
        </Typography.Text>
      )}

      {/* Formatted Display (on blur) */}
      {displayAmount && displayAmount > 0 && showFormatted && currencyInfo && (
        <Typography.Text type="success" style={{ fontWeight: 500, marginTop: -8, marginBottom: 8, display: 'block' }}>
          {(() => {
            const formatted = formatCurrencyDisplay(displayAmount, currencyCode, { 
              showDecimals: false, 
              absoluteValue: false, 
              compact: false 
            });
            return formatted.replace(/\s*LPA\s*/gi, '') + ' per annum';
          })()}
        </Typography.Text>
      )}
    </>
  );
};

export default CTCField;
